import React from 'react';
import { formatMexicoDateTime as formatDate, getTodayInMexico } from '../../dateUtils';
import { Users, AlertTriangle, CheckCircle, Download } from 'lucide-react';

const TicketsTable = ({ tickets = [], cardClasses }) => {
  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'Critica': return 'bg-red-500/20 text-red-400';
      case 'Alta': return 'bg-orange-500/20 text-orange-400';
      case 'Media': return 'bg-yellow-500/20 text-yellow-400';
      case 'Baja': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusColor = (estado) => {
    switch (estado) {
      case 'Cerrado':
      case 'Solucionado':
        return 'bg-green-500/20 text-green-400';
      case 'En curso':
      case 'Pendiente por usuario':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'Cancelado':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const exportToCSV = () => {
    // Definir las columnas que queremos exportar
    const headers = [
      'ID Solicitud',
      'Asunto',
      'Tipo de ticket',
      'Producto',
      'Cuenta',
      'Prioridad',
      'Estado',
      'Técnico',
      'Grupo de Soporte',
      'Tiempo Transcurrido (h)',
      'SLA (h)',
      'Hora de Creación (America/Mexico_City)',
      'Hora de Finalización (America/Mexico_City)'
    ];

    // Convertir los datos a formato CSV
    const csvContent = [
      headers.join(','),
      ...tickets.map(ticket => [
        ticket.ID_Solicitud,
        `"${ticket.Asunto || ''}"`,
        `"${ticket.Tipo_de_ticket || ''}"`,
        `"${ticket.Producto || ''}"`,
        `"${ticket.Cuenta || ''}"`,
        ticket.Prioridad || '',
        `"${ticket.Estado_de_solicitud || ''}"`,
        `"${ticket.Tecnico || ''}"`,
        `"${ticket.Grupo_de_Soporte || ''}"`,
        ticket.Tiempo_de_respuesta_transcurrido || '',
        ticket.Tiempo_de_respuesta_de_SLA || '',
        `"${formatDate(ticket.Hora_de_creacion)}"`,
        `"${formatDate(ticket.Hora_de_finalizacion)}"`
      ].join(','))
    ].join('\n');

    // Crear un Blob con el contenido CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Crear un enlace temporal para descargar
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `tickets_${getTodayInMexico()}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`${cardClasses} p-6 rounded-2xl mb-8`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold flex items-center">
          <Users className="w-6 h-6 mr-2 text-green-400" />
          Tickets Recientes ({tickets.length})
        </h3>
        
        <button
          onClick={exportToCSV}
          className="flex items-center space-x-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 px-4 py-2 rounded-lg transition-colors duration-200 font-semibold"
        >
          <Download className="w-4 h-4" />
          <span>Exportar CSV</span>
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-300/20">
              <th className="text-left py-3 px-4 font-semibold">ID</th>
              <th className="text-left py-3 px-4 font-semibold">Asunto</th>
              <th className="text-left py-3 px-4 font-semibold">Cuenta</th>
              <th className="text-left py-3 px-4 font-semibold">Prioridad</th>
              <th className="text-left py-3 px-4 font-semibold">Estado</th>
              <th className="text-left py-3 px-4 font-semibold">Técnico</th>
              <th className="text-left py-3 px-4 font-semibold">SLA</th>
              <th className="text-left py-3 px-4 font-semibold">Creado</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket, index) => {
              const isOverSLA = parseFloat(ticket.Tiempo_de_respuesta_transcurrido || 0) > 
                               parseFloat(ticket.Tiempo_de_respuesta_de_SLA || 0);
              
              return (
                <tr key={index} className="border-b border-gray-300/10 hover:bg-white/5 transition-colors duration-200">
                  <td className="py-4 px-4">
                    <a
                      href={`https://kionetworks.n.technology/WorkOrder.do?woMode=viewWO&woID=${ticket.ID_Solicitud}&PORTALID=1`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 hover:underline cursor-pointer font-semibold transition-colors duration-200"
                    >
                      #{ticket.ID_Solicitud}
                    </a>
                    <div className="text-sm opacity-70">{ticket.Tipo_de_ticket || 'Solicitud'}</div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="font-medium">{ticket.Asunto}</div>
                    <div className="text-sm opacity-70">{ticket.Producto}</div>
                  </td>
                  <td className="py-4 px-4 font-medium">{ticket.Cuenta}</td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(ticket.Prioridad)}`}>
                      {ticket.Prioridad}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(ticket.Estado_de_solicitud)}`}>
                      {ticket.Estado_de_solicitud}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="font-medium">{ticket.Tecnico}</div>
                    <div className="text-sm opacity-70">{ticket.Grupo_de_Soporte}</div>
                  </td>
                  <td className="py-4 px-4">
                    <div className={`flex items-center space-x-2 ${isOverSLA ? 'text-red-400' : 'text-green-400'}`}>
                      <span className="text-sm font-medium">
                        {ticket.Tiempo_de_respuesta_transcurrido || 'N/A'}h / {ticket.Tiempo_de_respuesta_de_SLA || 'N/A'}h
                      </span>
                      {isOverSLA ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm font-medium">{formatDate(ticket.Hora_de_creacion)}</div>
                    {ticket.Hora_de_finalizacion && (
                      <div className="text-xs opacity-70">Fin: {formatDate(ticket.Hora_de_finalizacion)}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TicketsTable;